import moment from 'moment-timezone';

declare const dayKeyBrand: unique symbol;

export type DayKey = string & { readonly [dayKeyBrand]: true };
export type EpochMs = number;
export type InstantInput = string | EpochMs | Date;

const DAY_KEY_FORMAT = 'YYYY-MM-DD';
const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const RFC_3339_INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const requireTimeZone = (timezone: string): void => {
  if (!isValidTimeZone(timezone)) throw new RangeError(`Invalid timezone: ${timezone}`);
};

const requireDayKey = (day: DayKey): DayKey => {
  const parsed = parseDayKey(String(day));
  if (!parsed) throw new RangeError(`Invalid organization day: ${String(day)}`);
  return parsed;
};

export const parseDayKey = (value: string): DayKey | null => {
  if (!DAY_KEY_PATTERN.test(value)) return null;
  const parsed = moment.utc(value, DAY_KEY_FORMAT, true);
  return parsed.isValid() && parsed.format(DAY_KEY_FORMAT) === value ? (value as DayKey) : null;
};

export const isValidTimeZone = (timezone: string): boolean => Boolean(timezone && moment.tz.zone(timezone));

export const addDays = (day: DayKey, days: number): DayKey => {
  if (!Number.isSafeInteger(days)) throw new RangeError('Day offset must be a safe integer');
  const validDay = requireDayKey(day);
  return moment.utc(validDay, DAY_KEY_FORMAT, true).add(days, 'days').format(DAY_KEY_FORMAT) as DayKey;
};

export const dayKeyToSyntheticUtcMs = (day: DayKey): EpochMs => {
  const validDay = requireDayKey(day);
  const [year, month, date] = validDay.split('-').map(Number);
  return Date.UTC(year, month - 1, date);
};

// Some zones advance at 00:00; parsing the day resolves to its first real instant.
export const orgDayStartMs = (day: DayKey, timezone: string): EpochMs => {
  requireTimeZone(timezone);
  const validDay = requireDayKey(day);
  const start = moment.tz(validDay, DAY_KEY_FORMAT, true, timezone);
  if (!start.isValid() || start.format(DAY_KEY_FORMAT) !== validDay) {
    throw new RangeError(`Organization day does not exist in timezone: ${validDay}`);
  }
  return start.valueOf();
};

export const syntheticUtcMsToDayKey = (coordinate: EpochMs): DayKey => {
  if (!Number.isFinite(coordinate)) throw new RangeError('Chart coordinate must be a finite epoch-millisecond value');
  return new Date(coordinate).toISOString().slice(0, 10) as DayKey;
};

export const formatDayKey = (day: DayKey, options: Intl.DateTimeFormatOptions, locale = 'en-US'): string =>
  new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: 'UTC',
  }).format(new Date(dayKeyToSyntheticUtcMs(day)));

export const orgDayKey = (instant: InstantInput, timezone: string): DayKey | null => {
  requireTimeZone(timezone);

  let parsed: moment.Moment;
  if (typeof instant === 'number') {
    if (!Number.isFinite(instant)) return null;
    parsed = moment.tz(instant, timezone);
  } else if (instant instanceof Date) {
    if (!Number.isFinite(instant.getTime())) return null;
    parsed = moment.tz(instant.getTime(), timezone);
  } else {
    if (!RFC_3339_INSTANT_PATTERN.test(instant)) return null;
    parsed = moment.parseZone(instant, moment.ISO_8601, true).tz(timezone);
  }

  return parsed.isValid() ? (parsed.format(DAY_KEY_FORMAT) as DayKey) : null;
};

export const orgToday = (timezone: string, now: EpochMs): DayKey => {
  const today = orgDayKey(now, timezone);
  if (!today) throw new RangeError('Current time must be a finite epoch-millisecond value');
  return today;
};

export const orgDayRange = ({ timezone, days, now }: { timezone: string; days: number; now: EpochMs }): DayKey[] => {
  if (!Number.isSafeInteger(days) || days < 1) throw new RangeError('Days must be a positive safe integer');
  const today = orgToday(timezone, now);
  return Array.from({ length: days }, (_, index) => addDays(today, index - days + 1));
};

// How date-times written without an offset look, as a `datetime-local` input produced them.
const LOCAL_DATE_TIME_FORMATS = ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss'];

/**
 * A stored date-time as a moment in `timezone`. An RFC 3339 value keeps its instant; a value without an offset is read as
 * that zone's local time. Null for anything else, and for an invalid zone.
 */
export const parseOrgDateTime = (value: unknown, timezone: string): moment.Moment | null => {
  if (typeof value !== 'string' || !isValidTimeZone(timezone)) return null;
  const text = value.trim();
  const parsed = RFC_3339_INSTANT_PATTERN.test(text)
    ? moment.parseZone(text, moment.ISO_8601, true).tz(timezone)
    : moment.tz(text, LOCAL_DATE_TIME_FORMATS, true, timezone);
  return parsed.isValid() ? parsed : null;
};
